import { useQuery } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"
import { Person } from "@/types/person"

import type { Enums } from "../../database.types"

interface UsePeopleDataCompletenessOptions {
  limit?: number
  offset?: number
  searchQuery?: string
  statusFilter?: string
  locationFilter?: string
  driverManagerTeamFilter?: string
  sheriffTypeFilter?: string
  dspFilter?: string
}

export function usePeopleDataCompleteness({
  limit,
  offset = 0,
  searchQuery = "",
  statusFilter = "Active",
  locationFilter = "",
  driverManagerTeamFilter = "",
  sheriffTypeFilter = "",
  dspFilter = "",
}: UsePeopleDataCompletenessOptions = {}) {
  return useQuery({
    queryKey: [
      "people-data-completeness",
      searchQuery,
      statusFilter,
      locationFilter,
      driverManagerTeamFilter,
      sheriffTypeFilter,
      dspFilter,
      limit,
      offset,
    ],
    queryFn: async () => {
      // Build base query
      let baseQuery = supabase
        .from("people")
        .select(
          `
                    id,
                    person_id,
                    first_name,
                    surname,
                    date_of_birth,
                    email,
                    address_line_1,
                    city,
                    postcode,
                    phone_number,
                    bank_name,
                    name_on_bank_account,
                    bank_account_number,
                    bank_sort_code,
                    contract_start_date,
                    pay_basis,
                    pay_group,
                    sheriff_areas,
                    status,
                    location,
                    dsp_id,
                    driver_manager_team,
                    sheriff_type
                `,
          { count: "exact" },
        )
        .not("person_id", "is", null)
        .order("first_name", { ascending: true })
        .order("surname", { ascending: true })

      // Apply status filter
      if (statusFilter && statusFilter !== "all") {
        baseQuery = baseQuery.eq("status", statusFilter)
      }

      // Apply search query
      if (searchQuery) {
        // Try to match person_id as exact number first, then fall back to text search
        const personIdNum = Number(searchQuery)
        if (!isNaN(personIdNum) && personIdNum.toString() === searchQuery.trim()) {
          // Exact person_id match
          baseQuery = baseQuery.or(
            `first_name.ilike.%${searchQuery}%,surname.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%,person_id.eq.${personIdNum}`,
          )
        } else {
          // Text search only
          baseQuery = baseQuery.or(
            `first_name.ilike.%${searchQuery}%,surname.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%`,
          )
        }
      }

      // Apply location filter
      if (locationFilter) {
        baseQuery = baseQuery.contains("location", [locationFilter])
      }

      // Apply sheriff type filter
      if (sheriffTypeFilter) {
        baseQuery = baseQuery.eq("sheriff_type", sheriffTypeFilter as Enums<"person_type">)
      }

      // Apply driver manager team filter
      if (driverManagerTeamFilter) {
        baseQuery = baseQuery.eq("driver_manager_team", driverManagerTeamFilter)
      }

      // Apply DSP filter
      if (dspFilter) {
        baseQuery = baseQuery.eq("dsp_id", dspFilter)
      }

      // If limit is provided, use pagination; otherwise fetch all records
      if (limit !== undefined && limit > 0) {
        const query = baseQuery.range(offset, offset + limit - 1)
        const { data, error, count } = await query

        if (error) {
          throw new Error(`Failed to fetch people data: ${error.message}`)
        }

        return {
          records: (data || []) as Person[],
          total: count || 0,
        }
      } else {
        // Fetch all records by paginating through all pages
        const allRecords: Person[] = []
        const pageSize = 1000
        let currentOffset = 0
        let hasMore = true
        let totalCount = 0

        while (hasMore) {
          const query = baseQuery.range(currentOffset, currentOffset + pageSize - 1)
          const { data, error, count } = await query

          if (error) {
            throw new Error(`Failed to fetch people data: ${error.message}`)
          }

          if (count !== null && totalCount === 0) {
            totalCount = count
          }

          if (data && data.length > 0) {
            allRecords.push(...(data as Person[]))
            hasMore = data.length === pageSize
            currentOffset += pageSize
          } else {
            hasMore = false
          }
        }

        return {
          records: allRecords,
          total: totalCount || allRecords.length,
        }
      }
    },
  })
}
